// Reine Gruppierungslogik für die Editor-Feldliste (DbEditorV2).
//
// Die Feldliste wird nach Feldgruppen (settings.fieldGroups, in definierter
// Reihenfolge) gegliedert: pro Gruppe ein Kopf + darunter die zugeordneten
// Felder. Felder ohne Gruppe (und virtuelle Template-Spalten) landen in einer
// eigenen Sektion „Ohne Feldgruppe" am Ende. Sortiert wird NUR innerhalb einer
// Gruppe — jede Sektion ist im Editor eine eigene VueDraggable-Instanz (kein
// geteilter group-Name), d.h. gruppenübergreifendes Ziehen ist strukturell
// nicht möglich. Ohne definierte Feldgruppen bleibt die flache Darstellung
// ohne Köpfe erhalten.
//
// Hier bewusst als reine Funktion (kein Vue) extrahiert → per Vitest testbar.

export interface FieldGroupDef {
  id?: string | null;
  name?: string | null;
}

export interface BuiltFieldSection<R> {
  key: string;
  title: string | null;
  rows: R[];
}

/**
 * Gruppiert `rows` anhand der `groups` in Sektionen.
 *
 * @param rows       die (bereits sortierte) flache Zeilenliste
 * @param groups     die Feldgruppen in Anzeige-Reihenfolge
 * @param groupIdOf  liefert die Gruppen-Id einer Zeile (undefined/null =
 *                   ungruppiert; virtuelle Zeilen immer ungruppiert)
 */
export function buildFieldSections<R>(
  rows: R[],
  groups: FieldGroupDef[] | null | undefined,
  groupIdOf: (row: R) => string | null | undefined,
): BuiltFieldSection<R>[] {
  const defined = groups ?? [];
  // Keine Gruppen → flache Einzel-Sektion ohne Kopf (gleiche Array-Identität,
  // damit In-place-Reorder im Editor unverändert funktioniert).
  if (!defined.length) {
    return [{ key: '__all__', title: null, rows }];
  }
  const buckets: Record<string, R[]> = {};
  defined.forEach((g) => {
    if (g.id) buckets[g.id] = [];
  });
  const ungrouped: R[] = [];
  for (const row of rows) {
    const gid = groupIdOf(row);
    if (gid && buckets[gid]) buckets[gid].push(row);
    else ungrouped.push(row);
  }
  const result: BuiltFieldSection<R>[] = defined
    .filter((g) => !!g.id)
    .map((g) => ({ key: g.id as string, title: g.name || 'Feldgruppe', rows: buckets[g.id as string] }));
  if (ungrouped.length) {
    result.push({ key: '__ungrouped__', title: 'Ohne Feldgruppe', rows: ungrouped });
  }
  return result;
}

/**
 * Die Felder in der Reihenfolge, in der die Maske „Datenfelder" sie zeigt.
 *
 * ═══ DIE REGEL DES BETREIBERS (01.09.2026) ════════════════════════════════
 *
 * „Anzeige in der Reihenfolge wie in der Liste der Datenfelder."
 *
 * Bis dahin nahm die Listenansicht die Felder in der ROHEN Reihenfolge, in der
 * sie aus der Datenbank kommen. Die Maske zeigt sie aber nach Feldgruppen
 * gegliedert. Zwei Reihenfolgen, und die sichtbare war nicht die wirksame.
 *
 * GEMESSEN am Ticketsystem: Die Maske beginnt mit „Titel, Ticket-Nr., Ticket
 * ausführlich" (Gruppe „Das Ticket"), die Liste zeigte „Art, Titel, Status,
 * Priorität". Weil die Ansicht zusätzlich nach der eingestellten Spaltenzahl
 * abschneidet, fielen ausgerechnet die Felder heraus, die in der Maske ganz
 * oben stehen — „Ticket ausführlich" und „Sprint". Der Betreiber sah eine
 * Ansicht, die sich an nichts zu halten schien; sie hielt sich an etwas, das
 * man nicht sehen konnte.
 *
 * ÜBER `buildFieldSections`, NICHT NACHGEBAUT: Wäre die Reihenfolge hier ein
 * zweites Mal ausformuliert, liefe sie beim nächsten Umbau der Maske wieder
 * auseinander — genau der Fehler, der behoben wird.
 */
export function felderInMaskenreihenfolge<F extends { fieldGroup?: string | null }>(
  felder: F[] | null | undefined,
  gruppen: FieldGroupDef[] | null | undefined,
): F[] {
  const liste = felder ?? [];
  if (!liste.length) return [];
  return buildFieldSections(liste, gruppen, (f) => f.fieldGroup)
    .flatMap((abschnitt) => abschnitt.rows);
}
